import { computed, ref } from 'vue'
import { useUserExtendedData } from '@/composables/useUserExtendedData'
import { useAccess } from '@/composables/useAccess'
import { useNavigation } from '@/composables/useNavigation'
import type { NavigationMenuItem } from './types'
import type { Resource } from '@/types/_general'

export function useMenuItems() {
  const userExtendedDataQuery = useUserExtendedData()
  const { canAccessResource, canCallMethod } = useAccess()
  const { getMenuItemTo } = useNavigation()

  const lastValidMenu = ref<NavigationMenuItem[]>([])

  function isLeafItem(item: NavigationMenuItem): boolean {
    return (
      (item.sub_menu == null || item.sub_menu.length === 0) && (item.list_menu == null || item.list_menu.length === 0)
    )
  }

  function shouldIncludeItem(item: NavigationMenuItem): boolean {
    if (!hasAccessToMenuItemResource(item)) return false
    return !(isLeafItem(item) && getMenuItemTo(item) == null)
  }

  function hasAccessToMenuItemResource(item: NavigationMenuItem): boolean {
    if (item.name === 'ItemSearch' && item.params?.resource != null) {
      return (
        canAccessResource(() => item.params.resource as Resource).value &&
        canCallMethod('get', () => `/${item.params.resource}`).value
      )
    }
    return true
  }

  function filterInaccessibleMenuItems(items: NavigationMenuItem[]): NavigationMenuItem[] {
    const result: NavigationMenuItem[] = []

    for (const item of items) {
      if (!shouldIncludeItem(item)) continue

      const filtered = { ...item }

      if (filtered.sub_menu != null && filtered.sub_menu.length > 0) {
        filtered.sub_menu = filterInaccessibleMenuItems(filtered.sub_menu)
        if (filtered.sub_menu.length === 0) continue
      }

      if (filtered.list_menu != null && filtered.list_menu.length > 0) {
        filtered.list_menu = filtered.list_menu.filter(shouldIncludeItem)
        if (filtered.list_menu.length === 0) continue
      }

      result.push(filtered)
    }

    return result
  }

  const menu = computed(() => {
    let newMenu: NavigationMenuItem[] = []

    const fallbackData = userExtendedDataQuery.data.value
    if (fallbackData) {
      const fallbackMenu = fallbackData.ui_structure?.navigation_menu
      if (Array.isArray(fallbackMenu)) {
        newMenu = [...fallbackMenu].sort((a, b) => {
          if (a.locale_name === 'common.other') return 1
          if (b.locale_name === 'common.other') return -1
          return 0
        })
      }
    }

    newMenu = filterInaccessibleMenuItems(newMenu)

    if (newMenu.length > 0) {
      lastValidMenu.value = newMenu
    }

    return newMenu.length > 0 ? newMenu : lastValidMenu.value
  })

  const isReady = computed(
    () => !userExtendedDataQuery.isFetching.value && !userExtendedDataQuery.isError.value && menu.value.length > 0
  )

  return {
    menu,
    isReady,
  }
}
